import React from "react";
import APIService from "../../Service/APIService";
import { Button, Badge, Input, Flex, Spin, Pagination, Skeleton } from "antd";
import {
  LoadingOutlined,
  ExclamationCircleFilled,
  CheckCircleFilled,
  ClockCircleFilled,
  FilterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import TitleBar from "../../common/title-bar/title-bar.js";
import GenericDashboard from "../GenericDashboard/GenericDashboard.js";
import { useScreen } from "../Utils/Screens.js";
import DateRangeFilter from "../Orders/DateRangeFilter.js";

const InvoiceDashboard = () => {
  const [formattedInvoices, setFormattedInvoices] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const requestIdRef = React.useRef(0);
  const [dateRange, setDateRange] = React.useState(null);

  const isPhone = !useScreen("sm");

  const roundToTwo = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    return Math.round(num * 100) / 100;
  };

  const hasInvoiceSentToCollection = (invoice) => {
    const value = invoice?.HasSentToCollection;
    if (value === true || value === 1) return true;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1";
    }
    return false;
  };

  const getInvoiceTotalWithCharges = (invoice) =>
    roundToTwo(
      roundToTwo(invoice?.InvoiceTotal) +
        roundToTwo(invoice?.ShippingCost) +
        roundToTwo(invoice?.CardProcessingFee) +
        roundToTwo(invoice?.BankProcessingFee)
    );

  const getInvoiceDashboardTotal = (invoice) =>
    hasInvoiceSentToCollection(invoice)
      ? roundToTwo(invoice?.InvoiceTotal)
      : getInvoiceTotalWithCharges(invoice);

  const getInvoiceDashboardStatus = (invoice) => {
    const invoiceTotalWithCharges = getInvoiceTotalWithCharges(invoice);
    const totalPaymentsApplied = roundToTwo(invoice?.TotalPaymentsApplied);
    const hasSentToCollection = hasInvoiceSentToCollection(invoice);
    const isVoided = Boolean(invoice?.IsVoided);
    const dueDate = invoice?.InvoiceDueDate ? dayjs(invoice.InvoiceDueDate) : null;
    const isPastDue =
      !dueDate || !dueDate.isValid()
        ? false
        : dueDate.startOf("day").isBefore(dayjs().startOf("day"));

    if (isVoided) {
      return {
        text: "Voided",
        statusClass: "voided",
        textColor: "lightText",
      };
    }

    if (hasSentToCollection) {
      return {
        text: "Sent To Collection",
        statusClass: "collections",
        textColor: "primaryColor",
      };
    }

    if (totalPaymentsApplied >= invoiceTotalWithCharges) {
      return {
        text: "Fully Paid",
        statusClass: "success",
        textColor: "successColor",
      };
    }

    if (isPastDue) {
      return {
        text: "Past Due",
        statusClass: "failure",
        textColor: "failureColor",
      };
    }

    return {
      text: "Open Balance",
      statusClass: "warning",
      textColor: "warningColor",
    };
  };

  const formatInvoices = (data) => {
    return (data || []).map((invoice) => {
      const totalDue = getInvoiceDashboardTotal(invoice);
      const dashboardInvoiceStatus = getInvoiceDashboardStatus(invoice);
      return {
        ...invoice,
        InvoiceNumber:
          invoice?.InvoiceNumber === null || invoice?.InvoiceNumber === undefined
            ? ""
            : invoice?.InvoiceNumber,
        InvoiceDate: dayjs(invoice.InvoiceDate).format("MM-DD-YYYY"),
        InvoiceDueDate: dayjs(invoice.InvoiceDueDate).format("MM-DD-YYYY"),
        InvoiceTotalNumber: totalDue,
        InvoiceTotal: `$${parseFloat(totalDue).toLocaleString("en-US", {
          maximumFractionDigits: 2,
        })}`,
        Icon: (
          <Badge
            count={
              dashboardInvoiceStatus.text === "Past Due" ? (
                <ExclamationCircleFilled style={{ color: "#ff0000" }} />
              ) : dashboardInvoiceStatus.text === "Open Balance" ? (
                <ClockCircleFilled style={{ color: "#ff9202" }} />
              ) : (
                <CheckCircleFilled style={{ color: "#27a907" }} />
              )
            }
            style={{ fontSize: "22px" }}
          />
        ),
        DashboardInvoiceStatus: dashboardInvoiceStatus,
      };
    });
  };

  const getStatusParam = (filter) => {
    switch (filter) {
      case "Open Balance":
        return "open_balance";
      case "Fully Paid":
        return "fully_paid";
      case "Past Due":
        return "past_due";
      case "Voided":
        return "voided";
      default:
        return "non_voided";
    }
  };

  const buildInvoiceUrl = (page, size, filters, statusFilter) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(size));
    if (filters?.startDate) params.set("startDate", filters.startDate);
    if (filters?.endDate) params.set("endDate", filters.endDate);
    params.set("status", getStatusParam(statusFilter));
    return `getinvoicedashboard?${params.toString()}`;
  };

  const fetchInvoicesPage = async (page, size, filters, statusFilter) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        buildInvoiceUrl(page, size, filters, statusFilter)
      );
      if (requestId !== requestIdRef.current) return;
      const payload = response?.data ?? {};
      const rows = payload?.data ?? [];
      const total = payload?.totalCount ?? rows.length;
      const pages = payload?.totalPages ?? Math.ceil(total / (size || 1));
      const formatted = formatInvoices(rows);
      setFormattedInvoices(formatted);
      setTotalCount(total);
      setTotalPages(pages);
    } catch (err) {
      console.error("Failed to fetch invoice dashboard data:", err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const [show, setShow] = React.useState(true);
  const [selectedFilter, setSelectedFilter] = React.useState("");

  React.useEffect(() => {
    if (searchKeyword !== "") return;
    fetchInvoicesPage(currentPage, pageSize, dateRange, selectedFilter);
  }, [searchKeyword, currentPage, pageSize, dateRange, selectedFilter]);

  const [isFilter, setIsFilter] = React.useState(false);


  const handleFilter = (filter) => {
    if (searchKeyword) {
      requestIdRef.current += 1;
      setSearchKeyword("");
    }
    if (filter === "Clear") {
      setIsFilter(false);
      setSelectedFilter("");
      setCurrentPage(1);
      return;
    }
    setIsFilter(true);
    setSelectedFilter(filter);
    setCurrentPage(1);
  };

  const handleSearch = async (val) => {
    const keyword = (val || "").trim();
    setSearchKeyword(keyword);
    if (keyword === "") {
      requestIdRef.current += 1;
      setCurrentPage(1);
      return;
    }
    if (isFilter) {
      setIsFilter(false);
      setSelectedFilter("");
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        `search?screen=InvoiceDashboard&word=${encodeURIComponent(keyword)}`
      );
      if (requestId !== requestIdRef.current) return;
      const data = response?.data?.data ?? [];
      const formatted = formatInvoices(data);
      setFormattedInvoices(formatted);
      setTotalCount(response?.data?.totalCount ?? formatted.length);
      setTotalPages(
        response?.data?.totalPages ?? Math.ceil(formatted.length / pageSize)
      );
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };
  const handleDateFilterChange = async (dateRange) => {
    setDateRange(dateRange);
    setCurrentPage(1);
  };

  const handlePaginationChange = (page, size) => {
    const nextSize = size || pageSize;
    const sizeChanged = nextSize !== pageSize;
    if (sizeChanged) {
      setPageSize(nextSize);
      setCurrentPage(1);
      return;
    }
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const renderedRowData = formattedInvoices;

  return (
    <div>
      <TitleBar
        label="Invoices"
        invoiceNumber={
          loading ? (
            <Skeleton.Avatar
              active
              shape="circle"
              size={24}
              style={{ width: 24 }}
            />
          ) : (
            totalCount
          )
        }
        rightFilterBarComponents={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Input.Search
              placeholder="Search"
              // suffix={<SearchOutlined />}
              allowClear
              style={{ height: "36px" }}
              // onSearch={value => setSearchKeyword(value)}
              onChange={(e) => {
                if (e.target.value === "") {
                  handleSearch("");
                }
              }}
              onSearch={(value) => handleSearch(value)}
            />
          </div>
        }
      />

      {isPhone ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div>
            <DateRangeFilter
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              overflowX: "scroll",
              gap: "16px",
              scrollbarWidth: "none",
            }}
          >
            <Button
              ghost={selectedFilter === "Open Balance" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Open Balance")}
            >
              <Badge
                count={
                  show ? <ClockCircleFilled style={{ color: "#ff9202" }} /> : 0
                }
              />
              Open Balance
            </Button>
            <Button
              ghost={selectedFilter === "Fully Paid" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Fully Paid")}
            >
              <Badge
                count={
                  show ? <CheckCircleFilled style={{ color: "#27a907" }} /> : 0
                }
              />
              Fully Paid
            </Button>
            <Button
              ghost={selectedFilter === "Past Due" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Past Due")}
            >
              <Badge
                count={
                  show ? (
                    <ExclamationCircleFilled style={{ color: "#ff0000" }} />
                  ) : (
                    0
                  )
                }
              />
              Past Due
            </Button>
            <Button
              ghost={selectedFilter === "Voided" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Voided")}
            >
              Voided
            </Button>
            {isFilter && (
              <Button
                ghost
                style={{ color: "#444", borderColor: "#444" }}
                onClick={() => handleFilter("Clear")}
              >
                Clear Filters <FilterOutlined />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "40px 0px 10px 0px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <DateRangeFilter
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
            <Button
              ghost={selectedFilter === "Open Balance" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Open Balance")}
            >
              <Badge
                count={
                  show ? <ClockCircleFilled style={{ color: "#ff9202" }} /> : 0
                }
              />
              Open Balance
            </Button>
            <Button
              ghost={selectedFilter === "Fully Paid" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Fully Paid")}
            >
              <Badge
                count={
                  show ? <CheckCircleFilled style={{ color: "#27a907" }} /> : 0
                }
              />
              Fully Paid
            </Button>
            <Button
              ghost={selectedFilter === "Past Due" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Past Due")}
            >
              <Badge
                count={
                  show ? (
                    <ExclamationCircleFilled style={{ color: "#ff0000" }} />
                  ) : (
                    0
                  )
                }
              />
              Past Due
            </Button>
            <Button
              ghost={selectedFilter === "Voided" ? false : true}
              style={{ color: "#444", borderColor: "#444" }}
              onClick={() => handleFilter("Voided")}
            >
              Voided
            </Button>
            {isFilter && (
              <Button
                ghost
                style={{ color: "#444", borderColor: "#444" }}
                onClick={() => handleFilter("Clear")}
              >
                Clear Filters <FilterOutlined />
              </Button>
            )}
          </div>
        </div>
      )}

      {loading && (
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      )}
      <GenericDashboard data={renderedRowData} screen="InvoiceDashboardNew" />
      {!loading && !searchKeyword && totalCount > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={["20", "50", "100"]}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
          />
        </div>
      )}
    </div>
  );
};

export default InvoiceDashboard;
